import { useContext, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { InpostGeowidgetReact } from 'inpost-geowidget-react'
import Switch from 'react-switch'
import { GetProfilesSubscription } from '../../../generated/urql.client'
import { useI18n } from '../../../provider/i18n'
import ProfileSelect from '../../../components/NewProfileSelect'
import useBasketData from '../../../hooks/useBasketData'
import { Link } from 'react-router-dom'
import { MdDelete, MdKeyboardArrowRight } from 'react-icons/md'
import { Blue300, Blue600, Green600, Red800 } from '../../../lib/colors'
import { AuthContext } from '../../../provider/auth'
import { laboratoryTestsToPriceString } from '../../../lib/test'
import {
  ConfirmDialog,
  ConfirmDialogType,
} from '../../../components/ConfirmDialog'
import InpostLogo from '../../../assets/inpost_logo.svg'
import Input from '../../../components/Input'
import { IoPhonePortrait, IoMail } from 'react-icons/io5'
import { MapPoint } from '../../../provider/basket'
import { GraphQLError } from 'graphql'
import { EmailRegex } from '../../../lib/patterns'
import { InpostGeowidget } from '../../../const'
import { sortProfile } from '../../../lib/sort'

type MapApi = {
  changeLanguage: (lang: string) => void
  changePointsType: () => void
  changePosition: () => void
  changeZoomLevel: () => void
  clearSearch: () => void
  hideSearchResult: () => void
  search: () => void
  selectPoint: (name: string) => void
  showPoint: (name: string) => void
  showPointDetails: () => void
}

const ShippingTests = () => {
  const i18n = useI18n()
  const [mapApi, setMapApi] = useState<MapApi>()
  const { isLogged } = useContext(AuthContext)
  const confirmDialogRef = useRef<ConfirmDialogType>(null)
  const {
    orderCost,
    orderCostError,
    shippingTests,
    shippingData,
    needsShippingToClient,
    form,
    setShippingData,
    changeItem,
    removeItem,
  } = useBasketData()
  const {
    register,
    formState: { errors },
  } = form

  const apiReady = (api: any) => setMapApi(api)

  const pointSelect = ({ name, address }: MapPoint) => {
    setShippingData({
      ...shippingData,
      provider: 'inpost_locker_standard',
      point: { name, address },
    })
  }

  const onChangePoint = () => {
    if (shippingData?.point && mapApi) {
      mapApi.showPoint(shippingData.point.name)
      setShippingData({ ...shippingData, point: undefined })
    }
  }

  useEffect(() => {
    mapApi?.changeLanguage?.(i18n.locale)
  }, [mapApi, i18n.locale])

  return (
    <div id="shipping-section" className="flex flex-col gap-2">
      <span className="text-lg md:text-xl font-bold">
        {i18n.t('screens.basket.sections.shippingTests.title')}
      </span>
      <ul className="flex flex-col gap-1">
        {shippingTests.sort(sortProfile).map((item, i) => {
          const testCost = !!orderCost?.shipping?.tests?.length
            ? orderCost.shipping.tests.find(
                ({ id }: any) => id === item.test.id
              )
            : undefined

          return (
            <li
              key={`test-${item.test.id}-${i}`}
              className="border-b border-b-border border-opacity-20 p-4 bg-white flex flex-col md:flex-row gap-4 items-center rounded-lg min-h-16 justify-center"
            >
              <div className="flex gap-4 w-full justify-between">
                <div className="flex flex-col justify-center gap-2">
                  <div>
                    <p className="text-lg font-bold leading-tight">
                      <Link
                        to={`/shipping-tests/${item.test.id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <span className="text-black">
                          {item.test.shortName || item.test.name}
                        </span>
                        <MdKeyboardArrowRight
                          size={20}
                          color={Blue600}
                          className="inline"
                        />
                      </Link>
                    </p>
                    {item.test.shortName &&
                      item.test.shortName !== item.test.name && (
                        <p className="text-blue-700 text-xs">
                          {item.test.name}
                        </p>
                      )}
                    {isLogged && !item.profile && (
                      <p className="text-red-700 text-xs">
                        {i18n.t(
                          'screens.basket.form.orderTests.assignToProfile'
                        )}
                      </p>
                    )}
                    {/* {isLogged && (
                      <label className="flex items-center gap-2 pt-2">
                        <Switch
                          checked={!!item.shouldShipToClient}
                          onChange={() =>
                            changeItem(item, {
                              ...item,
                              shouldShipToClient: !item.shouldShipToClient,
                            })
                          }
                          uncheckedIcon={false}
                          checkedIcon={false}
                          width={40}
                          height={24}
                          onColor={Green600}
                        />
                        <span className="text-xs text-blue-700">
                          {i18n.t(
                            'screens.basket.sections.shipping.needKitShipment'
                          )}
                        </span>
                      </label>
                    )} */}
                  </div>
                </div>
                <div className="flex flex-row items-center gap-4">
                  {isLogged && (
                    <ProfileSelect
                      onChange={(profile) =>
                        changeItem(item, {
                          ...item,
                          profile,
                        })
                      }
                      value={
                        item.profile as GetProfilesSubscription['client_profile'][number]
                      }
                      className="hidden md:block"
                    />
                  )}
                  <div>
                    {testCost && (
                      <div className="min-w-10">
                        <p className="text-base font-bold text-blue-700 whitespace-nowrap">
                          {i18n.t('screens.basket.form.orderTests.cost', {
                            cost: Number(testCost.cost).toFixed(2),
                          })}
                        </p>
                        {testCost.costBeforeDiscount && (
                          <p className="text-red-700 line-through text-right font-bold">
                            {i18n.t(
                              'screens.basket.form.orderTests.costBeforeDiscount',
                              {
                                cost: Number(
                                  testCost.costBeforeDiscount
                                ).toFixed(2),
                              }
                            )}
                          </p>
                        )}
                      </div>
                    )}
                    {!testCost && item.test.laboratoryTests && (
                      <p className="text-base font-bold text-blue-700 min-w-10 text-right whitespace-nowrap">
                        {i18n.t('screens.basket.form.orderTests.cost', {
                          cost: laboratoryTestsToPriceString(
                            item.test.laboratoryTests
                          ),
                        })}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      confirmDialogRef.current?.show({
                        message: i18n.t(
                          'screens.basket.form.orderTests.remove.alert.title',
                          {
                            testName: item.test.shortName || item.test.name,
                          }
                        ),
                        onSuccess: () => removeItem(item),
                      })
                    }}
                  >
                    <MdDelete size={24} color={Red800} />
                  </button>
                </div>
              </div>
              {isLogged && (
                <div className="flex flex-col sm:flex-row gap-2 sm:w-full md:hidden">
                  <ProfileSelect
                    onChange={(profile) =>
                      changeItem(item, {
                        ...item,
                        profile,
                      })
                    }
                    value={
                      item.profile as GetProfilesSubscription['client_profile'][number]
                    }
                    className="block md:hidden"
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {isLogged && (
        <>
          {needsShippingToClient && (
            <div className="border-b border-b-border border-opacity-20 bg-white flex flex-col items-start rounded-lg min-h-16">
              <div
                className={twMerge(
                  'flex justify-between w-full items-center text-blue-700 border-b border-b-border border-opacity-20 p-4',
                  !shippingData?.point && 'hidden'
                )}
              >
                <div className="flex flex-col ">
                  <span className="text-base">
                    {i18n.t('screens.basket.sections.shipping.pickup.selected')}
                    : <strong>{shippingData?.point?.name}</strong>
                  </span>
                  <span className="text-sm text-blue-700">
                    {shippingData?.point?.address.line1},{' '}
                    {shippingData?.point?.address.line2}
                  </span>
                </div>
                <button className="hl-button" onClick={onChangePoint}>
                  {i18n.t('common.change')}
                </button>
              </div>
              <div
                className={twMerge(
                  'w-full flex flex-col gap-4 text-blue-700 border-b border-b-border border-opacity-20 p-4',
                  !!shippingData?.point && 'hidden'
                )}
              >
                <span className="text-base font-bold">
                  Wybierz punkt odbioru
                </span>
                <div className="w-full h-[500px]">
                  <InpostGeowidgetReact
                    token={InpostGeowidget.token}
                    sandbox={InpostGeowidget.sandbox}
                    apiReady={apiReady}
                    pointSelect={pointSelect}
                  />
                </div>
              </div>
              <div className="w-full flex flex-col sm:flex-row sm:justify-between text-base text-blue-700 gap-4 p-4">
                <div className="flex flex-col gap-2">
                  <span className="font-bold">
                    {i18n.t(
                      'screens.basket.sections.shipping.form.shippingData.title'
                    )}
                  </span>
                  <div className="flex gap-2 items-center w-full sm:w-[350px]">
                    <IoMail color={Blue300} size={28} />
                    <div className="w-full">
                      <Input
                        defaultValue={shippingData?.email}
                        placeholder={i18n.t(
                          'screens.basket.sections.shipping.form.email.placeholder'
                        )}
                        className={
                          'p-2 bg-blue-100 rounded-lg border-b-border w-full border-opacity-20 text-base'
                        }
                        {...register('shippingEmail', {
                          required: i18n.t(
                            'screens.complaint.form.email.rules.required'
                          ),
                          pattern: {
                            value: EmailRegex,
                            message: i18n.t(
                              'screens.complaint.form.email.rules.pattern'
                            ),
                          },
                        })}
                      />
                      {errors.shippingEmail && (
                        <p className="hl-input-error">
                          {errors.shippingEmail.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center w-full sm:w-[350px]">
                    <IoPhonePortrait color={Blue300} size={28} />
                    <div className="w-full">
                      <Input
                        defaultValue={shippingData?.phone}
                        placeholder={i18n.t(
                          'screens.basket.sections.shipping.form.phone.placeholder'
                        )}
                        className={
                          'p-2 bg-blue-100 rounded-lg border-b-border border-opacity-20 w-full text-base'
                        }
                        {...register('shippingPhone', {
                          required: i18n.t(
                            'components.addressSelectorModal.form.phoneNumber.rules.required'
                          ),
                          pattern: {
                            value: /^((00|\+)\d{1,3})? ?(\d[ -]?){9}$/,
                            message: i18n.t(
                              'components.addressSelectorModal.form.phoneNumber.rules.pattern'
                            ),
                          },
                        })}
                      />
                      {errors.shippingPhone && (
                        <p className="hl-input-error">
                          {errors.shippingPhone.message}
                        </p>
                      )}
                    </div>
                  </div>
                  {!!orderCostError?.graphQLErrors?.length &&
                    isShippingProviderNotAvailable(
                      orderCostError?.graphQLErrors?.[0]
                    ) && (
                      <p className="text-red-700 text-xs">
                        {i18n.t(
                          'screens.basket.sections.shipping.errors.providerUnavailable'
                        )}
                      </p>
                    )}
                </div>
                <div>
                  <span>
                    {i18n.t('screens.basket.sections.shipping.provider.by')}
                  </span>
                  <img width={200} src={InpostLogo} />
                </div>
              </div>
            </div>
          )}
          {!!orderCost?.shipping && (
            <div className="flex flex-col bg-white border-b border-b-border border-opacity-20 rounded-lg">
              {needsShippingToClient && (
                <div className="flex justify-between text-base text-blue-700 border-b border-b-border border-opacity-20 p-2 md:px-4">
                  <span>
                    {i18n.t('screens.basket.sections.shipping.cost.toClient')}
                  </span>
                  <span className="font-bold">
                    {orderCost.shipping.toClientCost} zł
                  </span>
                </div>
              )}
              <div className="flex justify-between text-base text-blue-700 p-2 md:px-4">
                <span>
                  {i18n.t('screens.basket.sections.shipping.cost.toLab')}
                </span>
                <span className="font-bold">
                  {orderCost.shipping.toLabCost} zł
                </span>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog ref={confirmDialogRef} />
    </div>
  )
}

const isShippingProviderNotAvailable = (
  error: GraphQLError
): error is GraphQLError & {
  extensions: {
    code: 'shipping-not-found'
    providerCode: string
  }
} => {
  return error.extensions?.code === 'shipping-not-found'
}

export default ShippingTests
